import {useEffect, useRef} from "react";
import {useLocation} from "react-router-dom-v5-compat";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";

/* Hook taking callback function to execute before the window unloads.
 *
 * This hook takes a callback function which is then invoked upon
 * navigation, a tab reload, or a tab/browser close. The callback
 * function will not be invoked if the component using this hook
 * is unmounted before one of the above events happens.
 * The callback function must be synchronous.
 *
 * @param beforeUnloadHandler
 * The function that will be called on the beforeunload event.
 */
export const useOnBeforeUnloadHandler = (
    beforeUnloadHandler: () => void,
): void => {
    const location = useLocation();
    const locationRef = useRef(location);

    useOnMountEffect(() => {
        window.addEventListener("beforeunload", beforeUnloadHandler);
    });

    useEffect(() => {
        if (locationRef.current !== location) {
            beforeUnloadHandler();
            locationRef.current = location;
        }

        return () => {
            window.removeEventListener("beforeunload", beforeUnloadHandler);
        };
    }, [location, beforeUnloadHandler]);
};
